/* ==========================================================================
   Interactive Engine - Nimbokit Minimalist Atelier
   ========================================================================== */

import { APPS_DATA, TRUST_PRINCIPLES, type AppCategory } from '../data/apps.ts';
import { ProductModalController } from './product-modal.ts';

const TOOL_ICONS: Record<string, string> = {
  'mindmap': '/icons/ideamap.svg',
  'multi-tasks-autoclick': '/icons/autoclick.png'
};

const CATEGORY_TINTS: Record<AppCategory, string> = {
  focus: 'rgba(16, 185, 129, 0.10)',
  creative: 'rgba(235, 95, 120, 0.10)',
  automation: 'rgba(37, 99, 235, 0.10)'
};

export class DigitalAtelier {
  private currentIndex: number = 0;
  private currentLang: 'vi' | 'en' = 'vi';
  private modalCtrl: ProductModalController;

  // DOM Elements
  private stageHeroEl!: HTMLElement;
  private stageIndexEl!: HTMLElement;
  private stageHeadingEl!: HTMLElement;
  private stageDescEl!: HTMLElement;
  private stageChipsEl: HTMLElement | null = null;
  private stageStatsEl: HTMLElement | null = null;
  private btnLaunchEl!: HTMLAnchorElement;
  private btnLandingEl: HTMLAnchorElement | null = null;
  private btnStageSpecsEl: HTMLButtonElement | null = null;
  private btnPrevEl: HTMLElement | null = null;
  private btnNextEl: HTMLElement | null = null;
  private stagePreviewImgEl: HTMLImageElement | null = null;
  private stageMascotImg!: HTMLImageElement;
  private dockShelfEl!: HTMLElement;
  private trustStripEl: HTMLElement | null = null;
  private langBtnEl: HTMLButtonElement | null = null;
  private langLabelEl: HTMLElement | null = null;

  constructor() {
    try {
      const saved = localStorage.getItem('nimbokit_lang');
      if (saved === 'en' || saved === 'vi') {
        this.currentLang = saved;
      }
    } catch {}
    this.bindDOMElements();
    this.modalCtrl = new ProductModalController();
    this.updateLangUI();
    this.renderDockKeys();
    this.renderTrustStrip();
    this.bindEvents();
    this.selectApp(0, false);
  }

  private bindDOMElements() {
    this.stageHeroEl = document.querySelector('#stage-hero')!;
    this.stageIndexEl = document.querySelector('#stage-index')!;
    this.stageHeadingEl = document.querySelector('#stage-heading')!;
    this.stageDescEl = document.querySelector('#stage-desc')!;
    this.stageChipsEl = document.querySelector('#stage-chips');
    this.stageStatsEl = document.querySelector('#stage-stats');
    this.btnLaunchEl = document.querySelector('#btn-stage-launch')!;
    this.btnLandingEl = document.querySelector('#btn-stage-landing');
    this.btnStageSpecsEl = document.querySelector('#btn-stage-specs');
    this.btnPrevEl = document.querySelector('#btn-prev-tool');
    this.btnNextEl = document.querySelector('#btn-next-tool');
    this.stagePreviewImgEl = document.querySelector('#stage-preview-img');
    this.stageMascotImg = document.querySelector('#stage-mascot-img')!;
    this.dockShelfEl = document.querySelector('#dock-hardware-shelf')!;
    this.trustStripEl = document.querySelector('#trust-strip');
    this.langBtnEl = document.querySelector('#lang-toggle-btn');
    this.langLabelEl = document.querySelector('#lang-label');
  }

  // Window Deck removed in favor of direct Left-Column Feature Stats

  public renderTrustStrip() {
    if (!this.trustStripEl) return;
    const isEn = this.currentLang === 'en';
    this.trustStripEl.innerHTML = TRUST_PRINCIPLES.map((item) => {
      const title = isEn ? item.en.title : item.title;
      const desc = isEn ? item.en.description : item.description;
      return `
        <article class="trust-card">
          <div class="trust-icon-box" aria-hidden="true">${item.iconSvg}</div>
          <h2 class="trust-title">${title}</h2>
          <p class="trust-desc">${desc}</p>
        </article>
      `;
    }).join('');
  }

  private updateLangUI() {
    document.documentElement.setAttribute('lang', this.currentLang);
    const isEn = this.currentLang === 'en';

    if (this.btnStageSpecsEl) {
      this.btnStageSpecsEl.textContent = isEn ? 'Specs' : 'Chi tiết';
    }
    if (this.btnLandingEl) {
      this.btnLandingEl.textContent = isEn ? 'About ↗' : 'Giới thiệu ↗';
    }

    if (this.langLabelEl) {
      this.langLabelEl.textContent = isEn ? 'VI' : 'EN';
    }
    if (this.langBtnEl) {
      this.langBtnEl.title = isEn
        ? 'Chuyển sang Tiếng Việt (Switch to Vietnamese)'
        : 'Chuyển sang Tiếng Anh (Switch to English)';
      this.langBtnEl.setAttribute('aria-label', isEn
        ? 'Switch to Vietnamese'
        : 'Đổi sang Tiếng Anh');
    }
    if (this.btnPrevEl) {
      this.btnPrevEl.setAttribute('aria-label', isEn ? 'Previous instrument' : 'Công cụ trước');
    }
    if (this.btnNextEl) {
      this.btnNextEl.setAttribute('aria-label', isEn ? 'Next instrument' : 'Công cụ tiếp theo');
    }
    this.updateFooterLangUI();
  }

  private updateFooterLangUI() {
    const isEn = this.currentLang === 'en';
    const manifestoTitle = document.querySelector('#footer-manifesto-title');
    const manifestoDesc = document.querySelector('#footer-manifesto-desc');
    const supportTitle = document.querySelector('#footer-support-title');
    const footerDiscordLink = document.querySelector<HTMLAnchorElement>('#footer-discord-link-btn');
    const principlesTitle = document.querySelector('#footer-principles-title');
    const principlesDesc = document.querySelector('#footer-principles-desc');
    const disclaimerText = document.querySelector('#footer-disclaimer-text');

    if (manifestoTitle) manifestoTitle.textContent = isEn ? 'Independent Craft · 100% Local-First' : 'Chế tác độc lập · Local-First 100%';
    if (manifestoDesc) manifestoDesc.textContent = isEn
      ? 'Your data stays on your machine. Zero telemetry, no subscriptions, zero cloud reliance.'
      : 'Dữ liệu nằm trên máy bạn, không theo dõi, không phí thuê bao, không phụ thuộc đám mây.';
    if (supportTitle) supportTitle.textContent = isEn ? 'Support' : 'Hỗ Trợ';
    if (footerDiscordLink) {
      footerDiscordLink.innerHTML = `<span>🎮 Discord</span>`;
    }
    if (principlesTitle) principlesTitle.textContent = isEn ? 'Quality Guarantee' : 'Cam Kết';
    if (principlesDesc) principlesDesc.textContent = isEn
      ? 'Thoroughly tested before release. Lifetime license includes all future bug fixes.'
      : 'Kiểm tra kỹ lưỡng trước khi phát hành. Bản quyền bao gồm toàn bộ bản vá lỗi tương lai.';
    if (disclaimerText) disclaimerText.textContent = isEn
      ? 'Legal Disclaimer: Nimbokit is an independent software studio. All product names, trademarks, and registered trademarks (including Google Drive, Windows Defender, Microsoft, OpenAI) are property of their respective owners. Brand references are solely for technical compatibility descriptions and do not imply endorsement or affiliation.'
      : 'Tuyên bố miễn trừ trách nhiệm pháp lý: Nimbokit là studio phát triển phần mềm độc lập. Tất cả tên sản phẩm, nhãn hiệu và nhãn hiệu đã đăng ký (bao gồm Google Drive, Windows Defender, Microsoft, OpenAI) đều là tài sản của chủ sở hữu tương ứng. Việc đề cập tên thương hiệu chỉ nhằm mục đích mô tả khả năng tương thích kỹ thuật và không ngụ ý bất kỳ sự liên kết hoặc chứng thực nào.';
  }

  private renderDockKeys() {
    if (!this.dockShelfEl) return;
    this.dockShelfEl.innerHTML = APPS_DATA.map((app, idx) => {
      const iconUrl = TOOL_ICONS[app.id] || '/icons/ideamap.svg';
      return `
        <button id="dock-key-${idx}" class="dock-tool-key" data-index="${idx}" title="${app.name}">
          <img src="${iconUrl}" alt="${app.name}" class="key-icon key-app-icon" width="20" height="20" />
          <span class="key-label">${app.name}</span>
        </button>
      `;
    }).join('');
  }

  public selectApp(index: number, _playSound: boolean = false) {
    if (index < 0 || index >= APPS_DATA.length) return;
    this.currentIndex = index;
    const app = APPS_DATA[index];
    const isEn = this.currentLang === 'en';

    // 0. Update active app data attribute for thematic atmosphere (e.g. pastel cloud tinting)
    document.documentElement.setAttribute('data-active-app', app.id);

    // 1. Update Dock Key active states
    const allKeys = this.dockShelfEl.querySelectorAll<HTMLElement>('.dock-tool-key');
    allKeys.forEach((key, idx) => {
      key.classList.toggle('active', idx === index);
      if (idx === index) {
        key.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    });

    // 2. Animate and Update Inspector Content
    this.stageHeadingEl.style.opacity = '0';
    this.stageHeadingEl.style.transform = 'translateY(6px)';

    setTimeout(() => {
      const formattedIndex = `${String(index + 1).padStart(2, '0')} / ${String(APPS_DATA.length).padStart(2, '0')}`;
      this.stageIndexEl.textContent = formattedIndex;

      this.stageHeadingEl.textContent = (isEn && app.en?.headline) ? app.en.headline : app.headline;
      this.stageDescEl.textContent = (isEn && app.en) ? app.en.description : app.description;

      // 1 Nút Dùng Thử Chính (CTA)
      const appUrl = app.links.app || '#';
      this.btnLaunchEl.href = appUrl;
      this.btnLaunchEl.textContent = (isEn && app.en?.appLabel)
        ? app.en.appLabel
        : (app.links.appLabel || (isEn ? 'Launch App' : 'Dùng thử ngay'));

      if (this.btnStageSpecsEl) {
        this.btnStageSpecsEl.textContent = isEn ? 'Specs' : 'Chi tiết';
      }

      if (this.btnLandingEl) {
        if (!app.links.landing || !app.links.landingLabel) {
          this.btnLandingEl.style.display = 'none';
        } else {
          this.btnLandingEl.href = app.links.landing;
          this.btnLandingEl.textContent = (isEn && app.en?.landingLabel)
            ? app.en.landingLabel
            : app.links.landingLabel;
          this.btnLandingEl.style.display = 'inline-flex';
        }
      }

      // Feature Chips: Horizontal Pills with Dot (matching multitasksautoclick.nimbokit.com)
      if (this.stageChipsEl) {
        const chips = (isEn && app.en?.chips) ? app.en.chips : app.chips;
        this.stageChipsEl.innerHTML = (chips || []).map(chip => `
          <span class="hero-chip">
            <span class="dot"></span>${chip}
          </span>
        `).join('');
      }

      // Stats: 3 Bold Numbers + Labels
      if (this.stageStatsEl && app.stats) {
        this.stageStatsEl.innerHTML = app.stats.map(s => {
          const num = isEn ? s.en.num : s.num;
          const label = isEn ? s.en.label : s.label;
          return `
            <div class="stat-item">
              <div class="num">${num}</div>
              <div class="label">${label}</div>
            </div>
          `;
        }).join('');
      }

      this.stageHeadingEl.style.opacity = '1';
      this.stageHeadingEl.style.transform = 'translateY(0)';
    }, 100);

    // 3. Update Mascot Hologram & Ambient Tint
    const tint = CATEGORY_TINTS[app.category] || CATEGORY_TINTS.focus;
    this.stageHeroEl.style.setProperty('--capsule-tint', tint);


    // Update UI Screenshot Preview & Mascot
    if (this.stagePreviewImgEl) {
      this.stagePreviewImgEl.style.opacity = '0';
      this.stagePreviewImgEl.style.transform = 'scale(0.97)';
      setTimeout(() => {
        if (this.stagePreviewImgEl) {
          this.stagePreviewImgEl.src = `/shots/${app.previewShot}`;
          this.stagePreviewImgEl.alt = isEn ? `Live UI of ${app.name}` : `Giao diện phần mềm ${app.name}`;
          this.stagePreviewImgEl.style.opacity = '1';
          this.stagePreviewImgEl.style.transform = 'scale(1)';
        }
      }, 100);
    }

    this.stageMascotImg.style.opacity = '0';
    this.stageMascotImg.style.transform = 'scale(0.92)';

    setTimeout(() => {
      this.stageMascotImg.src = `/mascots/${app.mascotPose}`;
      this.stageMascotImg.style.opacity = '1';
      this.stageMascotImg.style.transform = 'scale(1)';
    }, 120);
  }

  private bindEvents() {
    // Stage Specs Drawer click
    this.btnStageSpecsEl?.addEventListener('click', () => {
      const currentApp = APPS_DATA[this.currentIndex];
      this.modalCtrl.open(currentApp.id, this.currentLang);
    });

    // Dock Key click
    this.dockShelfEl.addEventListener('click', (e) => {
      const targetKey = (e.target as HTMLElement).closest<HTMLElement>('.dock-tool-key');
      if (targetKey) {
        const index = parseInt(targetKey.getAttribute('data-index') || '0', 10);
        this.selectApp(index);
      }
    });

    // Arrow Navigation click
    this.btnPrevEl?.addEventListener('click', () => {
      const prev = (this.currentIndex - 1 + APPS_DATA.length) % APPS_DATA.length;
      this.selectApp(prev);
    });

    this.btnNextEl?.addEventListener('click', () => {
      const next = (this.currentIndex + 1) % APPS_DATA.length;
      this.selectApp(next);
    });

    // Touch Swipe Gestures on Focus Stage
    let touchStartX = 0;
    let touchStartY = 0;

    this.stageHeroEl.addEventListener('touchstart', (e: TouchEvent) => {
      if (e.touches.length === 1) {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
      }
    }, { passive: true });

    this.stageHeroEl.addEventListener('touchend', (e: TouchEvent) => {
      if (e.changedTouches.length === 1) {
        const touchEndX = e.changedTouches[0].clientX;
        const touchEndY = e.changedTouches[0].clientY;
        const diffX = touchEndX - touchStartX;
        const diffY = touchEndY - touchStartY;

        // Trigger if predominantly horizontal and at least 40px
        if (Math.abs(diffX) > 40 && Math.abs(diffX) > Math.abs(diffY) * 1.5) {
          if (diffX < 0) {
            const next = (this.currentIndex + 1) % APPS_DATA.length;
            this.selectApp(next);
          } else {
            const prev = (this.currentIndex - 1 + APPS_DATA.length) % APPS_DATA.length;
            this.selectApp(prev);
          }
        }
      }
    }, { passive: true });

    // Keyboard Shortcuts Navigation
    window.addEventListener('keydown', (e) => {
      if (['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement).tagName)) return;

      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        const next = (this.currentIndex + 1) % APPS_DATA.length;
        this.selectApp(next);
        this.dockShelfEl.querySelector<HTMLElement>(`#dock-key-${next}`)?.focus();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = (this.currentIndex - 1 + APPS_DATA.length) % APPS_DATA.length;
        this.selectApp(prev);
        this.dockShelfEl.querySelector<HTMLElement>(`#dock-key-${prev}`)?.focus();
      } else if (e.key >= '1' && e.key <= '9') {
        const target = parseInt(e.key, 10) - 1;
        if (target < APPS_DATA.length) {
          this.selectApp(target);
        }
      } else if (e.key === ' ' || e.key === 'Enter') {
        if (document.activeElement?.tagName !== 'BUTTON' && document.activeElement?.tagName !== 'A') {
          e.preventDefault();
          this.btnLaunchEl.click();
        }
      }
    });

    // Language Toggle Click
    if (this.langBtnEl) {
      this.langBtnEl.addEventListener('click', () => {
        this.currentLang = this.currentLang === 'vi' ? 'en' : 'vi';
        try {
          localStorage.setItem('nimbokit_lang', this.currentLang);
        } catch {}
        this.updateLangUI();
        this.renderTrustStrip();
        this.modalCtrl.setLang(this.currentLang);
        this.selectApp(this.currentIndex, false);
      });
    }
  }
}
